import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";

const CRITERIA = [
  { key: "faisabilite", label: "Faisabilité" },
  { key: "acceptabilite", label: "Acceptabilité" },
  { key: "couts_vs_avantages", label: "Coûts vs avantages" },
  { key: "efficacite", label: "Efficacité" },
  { key: "durabilite", label: "Durabilité" },
];

const EMPTY = {
  description_action: "", cause_hfacs_associee: "", id_chemin: "", id_lien_causal: "",
  faisabilite: 3, acceptabilite: 3, couts_vs_avantages: 3, efficacite: 3, durabilite: 3,
  responsable: "", echeance: "",
};

export default function HfixActionDialog({ open, onOpenChange, paths, onSubmit, submitting, initialValues }) {
  const [form, setForm] = useState(EMPTY);

  useEffect(() => { if (open) setForm({ ...EMPTY, ...(initialValues || {}) }); }, [open, initialValues]);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const updateScore = (key, value) => setForm({ ...form, [key]: Number(value) });

  const total = CRITERIA.reduce((sum, c) => sum + (Number(form[c.key]) || 0), 0);

  const submit = (e) => {
    e.preventDefault();
    onSubmit({
      ...form,
      faisabilite: Number(form.faisabilite), acceptabilite: Number(form.acceptabilite),
      couts_vs_avantages: Number(form.couts_vs_avantages), efficacite: Number(form.efficacite),
      durabilite: Number(form.durabilite),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="hfix-action-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase">Nouvelle action HFIX</DialogTitle>
          <DialogDescription>Décrivez l'action corrective et notez-la sur 5 critères pour obtenir un score sur 25.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Action corrective</Label>
            <Textarea required rows={2} data-testid="hfix-form-description" value={form.description_action} onChange={update("description_action")} />
          </div>
          <div>
            <Label>Cause HFACS associée (libre)</Label>
            <Input data-testid="hfix-form-cause" value={form.cause_hfacs_associee} onChange={update("cause_hfacs_associee")} />
          </div>
          <div>
            <Label>Chemin critique lié</Label>
            <Select value={form.id_chemin} onValueChange={(v) => setForm({ ...form, id_chemin: v })}>
              <SelectTrigger data-testid="hfix-form-chemin-select"><SelectValue placeholder="Aucun / sélectionner un chemin" /></SelectTrigger>
              <SelectContent>
                {paths.map((p) => <SelectItem key={p.ID_CHEMIN} value={p.ID_CHEMIN}>{p.NOM_CHEMIN}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="border border-border rounded-md p-3 space-y-3">
            <p className="font-mono text-xs uppercase text-muted-foreground">Cotation HFIX (1 à 5 par critère)</p>
            {CRITERIA.map((c) => (
              <div key={c.key} className="flex items-center justify-between gap-3">
                <Label className="flex-1">{c.label}</Label>
                <div className="flex items-center gap-1" data-testid={`hfix-form-score-${c.key}`}>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      type="button"
                      key={n}
                      data-testid={`hfix-form-score-${c.key}-${n}`}
                      onClick={() => updateScore(c.key, n)}
                      className={`h-7 w-7 text-xs rounded-sm border transition-colors ${Number(form[c.key]) === n ? "bg-primary text-primary-foreground border-primary" : "bg-secondary text-secondary-foreground border-border hover:border-primary/50"}`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            <div className="flex items-center justify-between pt-2 border-t border-border/60">
              <span className="text-sm font-medium">Score total</span>
              <span data-testid="hfix-form-score-total" className="font-heading font-black text-lg text-accent">{total} / 25</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Responsable</Label>
              <Input data-testid="hfix-form-responsable" value={form.responsable} onChange={update("responsable")} />
            </div>
            <div>
              <Label>Échéance</Label>
              <Input type="date" data-testid="hfix-form-echeance" value={form.echeance} onChange={update("echeance")} />
            </div>
          </div>

          <Button type="submit" disabled={submitting || !form.description_action} data-testid="hfix-form-submit" className="w-full bg-primary hover:bg-primary/90 text-primary-foreground">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Créer l'action"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
